(() => {
  if (window.NightglowCartDrawerInitialized) return;
  window.NightglowCartDrawerInitialized = true;

  const desktopSplitQuery = window.matchMedia('(min-width: 1200px)');
  const ADDON_FETCH_SECTION = 'cart-drawer';

  const getDrawer = () => document.querySelector('cart-drawer[data-nightglow-cart-drawer]');
  const getState = (drawer = getDrawer()) => drawer?.querySelector('[data-nightglow-cart-state]');

  function isCustomCart(drawer = getDrawer()) {
    return getState(drawer)?.dataset.customCart === 'true';
  }

  function getAddonCards(drawer = getDrawer()) {
    return Array.from(drawer?.querySelectorAll('[data-nightglow-cart-addon]') || []);
  }

  function hasAddonCards(drawer = getDrawer()) {
    return getAddonCards(drawer).length > 0;
  }

  function syncDrawerLayoutState() {
    const drawer = getDrawer();
    if (!drawer) return;

    const hasAddons = isCustomCart(drawer) && hasAddonCards(drawer);
    drawer.classList.toggle('nightglow-cart-drawer--has-addons', hasAddons);

    const controls = drawer.querySelector('[data-nightglow-addon-controls]');
    if (controls) controls.classList.toggle('hidden', getAddonCards(drawer).length <= 1);
  }

  function usesVerticalAddonList(viewport) {
    const drawer = viewport?.closest('cart-drawer[data-nightglow-cart-drawer]');
    return Boolean(
      drawer?.classList.contains('nightglow-cart-drawer--has-addons') &&
      desktopSplitQuery.matches
    );
  }

  function updateAddonViewport(viewport) {
    if (!viewport) return;

    const track = viewport.querySelector('[data-nightglow-cart-addon-track]');
    if (!track) return;

    const vertical = usesVerticalAddonList(viewport);
    const current = vertical ? track.scrollTop : track.scrollLeft;
    const maxScroll = vertical
      ? Math.max(0, track.scrollHeight - track.clientHeight)
      : Math.max(0, track.scrollWidth - track.clientWidth);

    const atStart = current <= 2;
    const atEnd = maxScroll <= 2 || current >= maxScroll - 2;

    viewport.classList.toggle('is-at-start', atStart);
    viewport.classList.toggle('is-at-end', atEnd);

    const section = viewport.closest('[data-nightglow-cart-addons]');
    if (!section) return;

    const prev = section.querySelector('[data-nightglow-cart-addon-prev]');
    const next = section.querySelector('[data-nightglow-cart-addon-next]');

    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
  }

  function updateAllAddonViewports(root = document) {
    syncDrawerLayoutState();
    root.querySelectorAll?.('[data-nightglow-cart-addon-viewport]').forEach(updateAddonViewport);
  }

  function resetDrawerScroll(drawer = getDrawer()) {
    if (!drawer) return;

    const body = drawer.querySelector('[data-nightglow-cart-body]');
    const items = drawer.querySelector('[data-nightglow-cart-scroll]');
    const addonTrack = drawer.querySelector('[data-nightglow-cart-addon-track]');

    if (body) body.scrollTop = 0;
    if (items) items.scrollTop = 0;
    if (addonTrack) {
      addonTrack.scrollTop = 0;
      addonTrack.scrollLeft = 0;
    }
  }

  function scrollAddons(button, direction) {
    const section = button.closest('[data-nightglow-cart-addons]');
    const viewport = section?.querySelector('[data-nightglow-cart-addon-viewport]');
    const track = viewport?.querySelector('[data-nightglow-cart-addon-track]');
    const firstCard = track?.querySelector('[data-nightglow-cart-addon]');

    if (!viewport || !track || !firstCard) return;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.rowGap || styles.columnGap || styles.gap || 0) || 0;
    const vertical = usesVerticalAddonList(viewport);
    const rect = firstCard.getBoundingClientRect();
    const amount = (vertical ? rect.height : rect.width) + gap;

    track.scrollBy(
      vertical
        ? { top: direction * amount, behavior: 'smooth' }
        : { left: direction * amount, behavior: 'smooth' }
    );

    window.setTimeout(() => updateAddonViewport(viewport), 340);
  }

  async function fetchSectionHtml(url) {
    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Nightglow add-on fetch failed (${response.status})`);
    return response.text();
  }

  function parseFetchedAddonCards(html) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const container = documentFragment.querySelector('[data-nightglow-fetched-addons]');
    if (!container) return [];

    return Array.from(container.querySelectorAll('[data-nightglow-cart-addon]'));
  }

  async function fetchRecommendationAddons(source, state) {
    const recommendationsUrl = state?.dataset.recommendationsUrl;
    const productId = source?.dataset.productId;

    if (!recommendationsUrl || !productId) return [];

    const url = new URL(recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', ADDON_FETCH_SECTION);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'complementary');

    return parseFetchedAddonCards(await fetchSectionHtml(url));
  }

  function appendUniqueCards(track, cards, seenHandles, cartHandles) {
    if (!track) return 0;

    let added = 0;

    for (const card of cards) {
      const handle = card.dataset.nightglowAddonHandle;
      if (!handle || seenHandles.has(handle) || cartHandles.has(handle)) continue;

      seenHandles.add(handle);
      track.appendChild(card);
      added += 1;
    }

    return added;
  }

  async function hydrateMissingAddons() {
    const drawer = getDrawer();
    const state = getState(drawer);

    if (!drawer || !state || !isCustomCart(drawer)) return;
    if (hasAddonCards(drawer)) return;

    const section = drawer.querySelector('[data-nightglow-cart-addons][data-nightglow-addons-needs-fetch]');
    const track = section?.querySelector('[data-nightglow-cart-addon-track]');

    if (!section || !track || section.dataset.fetching === 'true') return;

    const sources = Array.from(state.querySelectorAll('[data-nightglow-addon-source]'));
    if (!sources.length) return;

    section.dataset.fetching = 'true';

    const cartHandles = new Set(
      sources.map((source) => source.dataset.productHandle).filter(Boolean)
    );
    const seenHandles = new Set();

    try {
      // Liquid remains the primary source for custom.add_on_products.
      // If the cart section reload did not resolve cards, use Shopify's
      // official product recommendations endpoint as a client-side fallback.
      for (const source of sources) {
        try {
          const cards = await fetchRecommendationAddons(source, state);
          appendUniqueCards(track, cards, seenHandles, cartHandles);
        } catch (error) {
          console.warn('Nightglow complementary add-on fetch skipped:', error);
        }
      }

      if (track.children.length) {
        section.hidden = false;
        section.removeAttribute('data-nightglow-addons-needs-fetch');
      }
    } finally {
      section.dataset.fetching = 'false';
      syncDrawerLayoutState();
      window.requestAnimationFrame(() => {
        const viewport = section.querySelector('[data-nightglow-cart-addon-viewport]');
        updateAddonViewport(viewport);
      });
    }
  }

  async function applyPromoCode(button) {
    const details = button.closest('[data-nightglow-promo-details]');
    const input = details?.querySelector('[data-nightglow-promo-input]');
    const message = details?.querySelector('[data-nightglow-promo-message]');
    const code = input?.value.trim();
    const cartDrawer = getDrawer();

    if (!input || !message || !cartDrawer) return;

    if (!code) {
      message.textContent = 'Enter a promo code.';
      input.focus();
      return;
    }

    button.setAttribute('aria-disabled', 'true');
    input.setAttribute('aria-busy', 'true');
    message.textContent = '';

    try {
      const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      const response = await fetch(window.routes.cart_update_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({
          discount: code,
          sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Unable to apply promo code');
      const state = await response.json();

      const matchingCode = Array.isArray(state.discount_codes)
        ? state.discount_codes.find(
            (entry) => String(entry.code || '').toLowerCase() === code.toLowerCase()
          )
        : null;

      if (matchingCode && matchingCode.applicable === false) {
        message.textContent = 'This promo code could not be applied.';
        return;
      }

      cartDrawer.renderContents(state);
    } catch (error) {
      console.error(error);
      message.textContent = 'We could not apply that promo code. Please try again.';
    } finally {
      button.removeAttribute('aria-disabled');
      input.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const previous = event.target.closest('[data-nightglow-cart-addon-prev]');
    if (previous) {
      event.preventDefault();
      scrollAddons(previous, -1);
      return;
    }

    const next = event.target.closest('[data-nightglow-cart-addon-next]');
    if (next) {
      event.preventDefault();
      scrollAddons(next, 1);
      return;
    }

    const promoButton = event.target.closest('[data-nightglow-promo-apply]');
    if (promoButton) {
      event.preventDefault();
      if (promoButton.getAttribute('aria-disabled') !== 'true') applyPromoCode(promoButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;

    const input = event.target.closest('[data-nightglow-promo-input]');
    if (!input) return;

    const details = input.closest('[data-nightglow-promo-details]');
    const button = details?.querySelector('[data-nightglow-promo-apply]');

    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    applyPromoCode(button);
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target.closest?.('[data-nightglow-cart-addon-track]');
      if (!track) return;

      updateAddonViewport(track.closest('[data-nightglow-cart-addon-viewport]'));
    },
    true
  );

  const refreshLayout = () => {
    window.requestAnimationFrame(() => {
      updateAllAddonViewports();
      hydrateMissingAddons();
    });
  };

  window.addEventListener('resize', refreshLayout);
  desktopSplitQuery.addEventListener?.('change', refreshLayout);

  const drawer = getDrawer();
  const cartDrawerElement = drawer?.querySelector('#CartDrawer');

  if (drawer) {
    new MutationObserver((mutations) => {
      const opened = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          drawer.classList.contains('active')
      );

      if (opened) {
        window.requestAnimationFrame(() => {
          resetDrawerScroll(drawer);
          updateAllAddonViewports(drawer);
        });
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  if (cartDrawerElement) {
    new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
        return;
      }

      window.requestAnimationFrame(() => {
        syncDrawerLayoutState();
        resetDrawerScroll(drawer);
        hydrateMissingAddons();
        updateAllAddonViewports(drawer);
      });
    }).observe(cartDrawerElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        resetDrawerScroll();
        refreshLayout();
      },
      { once: true }
    );
  } else {
    resetDrawerScroll();
    refreshLayout();
  }
})();
